import { actionDisplayName } from './actionName'
import type { WorkflowRunDetails } from '@/lib/workflowApi'
import {
  agentSpendOlives,
  isUnifiedAgentBilling,
  workflowRunRevisionRequestOf,
  type Action,
  type ActionPlanOverBudget,
  type ActionTraceEvent,
  type WorkflowRunItemRecord,
  type WorkflowRunRevisionRequest,
  type WorkflowRunWarning,
  type WorkflowRunStatus,
  type WorkflowRunStatusAction,
  type WorkflowRunStatusPlan,
  WorkflowProgressStage,
  WorkflowRunStatusSummary,
} from '@/types/shared'

export type { ActionTraceEvent }

/** A Go needs matching HTTP consent details and live canvas state. */
export function approvalSnapshotIsCurrent(
  details: WorkflowRunDetails | null,
  mirror: WorkflowRunStatus | null | undefined,
): boolean {
  if (!details || details.run.status !== 'awaiting_approval') return false
  const action = details.actions[details.run.currentActionIndex]
  if (!action || action.status !== 'awaiting_approval') return false
  return (
    !!mirror &&
    mirror.runId === details.run.id &&
    mirror.status === details.run.status &&
    mirror.currentActionId === action.actionId &&
    mirror.currentActionIndex === details.run.currentActionIndex
  )
}

// ---------------------------------------------------------------------------
// Status vocabulary — shared by the inspector, the workflow view, and the
// canvas overlay so an Action reads the same everywhere.
// ---------------------------------------------------------------------------

export interface StatusMeta {
  label: string
  /** Tailwind background for the 6px status dot. */
  dot: string
  /** Tailwind text color for the label. */
  text: string
  /** True while the harness is working on it — drives the spinner. */
  live: boolean
}

const STATUS_META: Record<string, StatusMeta> = {
  idle: { label: 'Not run yet', dot: 'bg-muted-foreground/40', text: 'text-muted-foreground', live: false },
  pending: { label: 'Waiting', dot: 'bg-muted-foreground/60', text: 'text-muted-foreground', live: false },
  // Not a stored status: a run waiting for a slot, read off its row by `runIsQueued`.
  queued: { label: 'Queued', dot: 'bg-muted-foreground/60', text: 'text-muted-foreground', live: false },
  running: { label: 'Running', dot: 'bg-foreground', text: 'text-foreground', live: true },
  awaiting_approval: { label: 'Needs approval', dot: 'bg-[#d4b65c]', text: 'text-[#d4b65c]', live: false },
  // The agent stopped on purpose; the inputs need work, not the run.
  needs_revision: { label: 'Needs revision', dot: 'bg-[#d4b65c]', text: 'text-[#d4b65c]', live: false },
  paused: { label: 'Paused', dot: 'bg-muted-foreground', text: 'text-muted-foreground', live: false },
  completed: { label: 'Done', dot: 'bg-[#6cb887]', text: 'text-foreground', live: false },
  failed: { label: 'Failed', dot: 'bg-[#d4736c]', text: 'text-[#d4736c]', live: false },
  cancelled: { label: 'Cancelled', dot: 'bg-muted-foreground', text: 'text-muted-foreground', live: false },
  skipped: { label: 'Skipped', dot: 'bg-muted-foreground', text: 'text-muted-foreground', live: false },
  rejected: { label: 'Rejected', dot: 'bg-[#d4736c]', text: 'text-[#d4736c]', live: false },
  superseded: { label: 'Superseded', dot: 'bg-muted-foreground', text: 'text-muted-foreground', live: false },
  rerunning: { label: 'Retaking', dot: 'bg-foreground', text: 'text-foreground', live: true },
  // A compile run seen from its Actions: the scripts are being prepared, no step runs yet.
  building: { label: 'Preparing scripts', dot: 'bg-foreground', text: 'text-foreground', live: true },
  scripts_built: { label: 'Scripts prepared', dot: 'bg-[#6cb887]', text: 'text-foreground', live: false },
  build_failed: { label: 'Preparation failed', dot: 'bg-[#d4736c]', text: 'text-[#d4736c]', live: false },
}

export function statusMeta(status: string): StatusMeta {
  return (
    STATUS_META[status] ?? {
      label: status.replace(/_/g, ' '),
      dot: 'bg-muted-foreground',
      text: 'text-muted-foreground',
      live: false,
    }
  )
}

export function runIsActive(status: string): boolean {
  return status === 'pending' || status === 'running' || status === 'paused' || status === 'awaiting_approval'
}

/**
 * A run waiting for a slot: asked for, not yet admitted by a worker. `pending` alone is not
 * enough — a resumed run passes through it with its start already stamped. The wait runs from
 * the run's creation to its start and is never part of its run time, which begins at `startedAt`.
 */
export function runIsQueued(run: { status: string; startedAt?: string | null }): boolean {
  return run.status === 'pending' && !run.startedAt
}

/** A wait shorter than this is dispatch latency, not a queue: a run that started this fast shows no queue row. */
export const QUEUE_WAIT_NOTICE_MS = 5_000

/** A stopped run the person can resume: broken, or waiting on a change to its inputs. */
export function runIsResumable(status: string): boolean {
  return status === 'failed' || status === 'needs_revision'
}

const OPEN_ACTION_STATUSES = new Set(['pending', 'running', 'awaiting_approval'])

/**
 * How an action reads inside its run. While the run is alive, the run row
 * carries paused / needs-approval for the current action. Once the run has
 * ended, an action row still open never finished on its own: a queued one
 * never ran and reads as cancelled, the current one ended with the run (a
 * cancel exits the executor without closing its row — observed as a cancelled
 * step spinning "Running" for six hours).
 */
export function actionStatusInRun(actionStatus: string, runStatus: string, isCurrent: boolean): string {
  // The run's index stays on its last action while the engine finishes up (a compile's selftest, a run's
  // final settle): a completed row is done, not "Running" with a frozen clock.
  if (actionStatus === 'completed') return 'completed'
  if (isCurrent && runIsActive(runStatus)) return runStatus
  if (!runIsActive(runStatus) && OPEN_ACTION_STATUSES.has(actionStatus)) return isCurrent ? runStatus : 'cancelled'
  return actionStatus
}

/**
 * Whether a mirrored run can stand for its Actions on the canvas. A build
 * (compile) run executes nothing — its agent walks the slots while it rewrites
 * the scripts — so once the scripts are built it stands for nothing: the run it
 * chained into does. (A compile that painted every card "Done" with empty bins
 * is what this rule prevents.) A live, failed, or cancelled build still stands,
 * read through buildActionStatus. Mirrored on the backend's prune in runMirror.ts.
 */
export function mirrorStands(status: Pick<WorkflowRunStatus, 'build' | 'status'>): boolean {
  return !status.build || status.status !== 'completed'
}

/**
 * Every member of a build run reads the build itself, never its own slot — the
 * same words as compileStatusLabel, so the inspector and the canvas agree. A
 * completed build reads "Scripts built" where it is still on screen (the
 * inspector, during the handoff to the chained run); the canvas never shows it
 * because mirrorStands drops it.
 */
export function buildActionStatus(status: Pick<WorkflowRunStatus, 'status'>): string {
  switch (status.status) {
    case 'completed':
      return 'scripts_built'
    case 'failed':
      return 'build_failed'
    case 'pending':
      return 'pending'
    default:
      return runIsActive(status.status) ? 'building' : status.status
  }
}

// ---------------------------------------------------------------------------
// The build step — a Workflow Beta compile rebuilding the scripts before the Actions run
// ---------------------------------------------------------------------------

export interface BuildStep {
  /** The compile's lifecycle: the run's own status while the compile is the run on screen; `completed` once its child runs. */
  status: string
  startedAt: string | null
  completedAt: string | null
  /** True while the panel shows the compile run itself rather than the run it chained into. */
  current: boolean
}

/**
 * The build as the run's first step: the compile run itself while it is the
 * run on screen, or the compile a chained run recorded at creation
 * (`options.compiledBy`). Null when no scripts were built for this run.
 */
export function buildStepOf(details: Pick<WorkflowRunDetails, 'run'> | null | undefined): BuildStep | null {
  if (!details) return null
  const options = details.run.input.options
  if (options?.compile === true) {
    return {
      status: details.run.status,
      startedAt: details.run.startedAt,
      completedAt: details.run.completedAt,
      current: true,
    }
  }
  if (options?.compiledBy) {
    return {
      status: 'completed',
      startedAt: options.compiledBy.startedAt,
      completedAt: options.compiledBy.completedAt,
      current: false,
    }
  }
  return null
}

export interface SelftestStep {
  status: 'idle' | 'running' | 'completed' | 'failed' | 'cancelled'
  startedAt: string | null
  completedAt: string | null
}

/**
 * The compile's selftest as the step after the last script: everything between the last script
 * landing and the verdict — the agent's own test turn, then the engine's verification
 * (`options.compileTests`, the durable stamp). Queued while scripts are still being written; on
 * its own clock from the last script; the engine's verdict closes it. Null off a compile run, and
 * for a finished compile with nothing to date it by.
 */
export function selftestStepOf(
  details: Pick<WorkflowRunDetails, 'run' | 'actions'> | null | undefined,
): SelftestStep | null {
  const options = details?.run.input.options
  if (!details || options?.compile !== true) return null
  const runStatus = details.run.status
  const tests = options.compileTests
  const scripts = details.actions
  const lastScriptAt =
    scripts.length > 0 && scripts.every(script => script.status === 'completed')
      ? scripts.reduce<string | null>(
          (latest, script) =>
            script.completedAt && (!latest || script.completedAt > latest) ? script.completedAt : latest,
          null,
        )
      : null
  const startedAt = [lastScriptAt, tests?.startedAt].filter((at): at is string => !!at).sort()[0] ?? null
  if (!startedAt) return runIsActive(runStatus) ? { status: 'idle', startedAt: null, completedAt: null } : null
  if (tests?.completedAt)
    return { status: tests.failed ? 'failed' : 'completed', startedAt, completedAt: tests.completedAt }
  if (runIsActive(runStatus)) return { status: 'running', startedAt, completedAt: null }
  const status: SelftestStep['status'] =
    runStatus === 'completed' ? 'completed' : runStatus === 'failed' ? 'failed' : 'cancelled'
  return { status, startedAt, completedAt: details.run.completedAt }
}

const SETUP_NOTE_CODES = new Set(['WORKFLOW_INITIAL_SETUP', 'WORKFLOW_SETUP_UPDATE'])

/** A lifecycle note the engine files as a warning ("this run prepares its scripts first"); not a caveat to count. */
export function isSetupNote(warning: Pick<WorkflowRunWarning, 'code'>): boolean {
  return SETUP_NOTE_CODES.has(warning.code)
}

/** A compile run's header: the run's states, named for what the run does — prepare the scripts. */
export function compileStatusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return 'Waiting to prepare'
    case 'running':
      return 'Preparing scripts'
    case 'completed':
      return 'Scripts prepared'
    case 'failed':
      return 'Preparation failed'
    case 'cancelled':
      return 'Preparation cancelled'
    default:
      return statusMeta(status).label
  }
}

// ---------------------------------------------------------------------------
// Time and money
// ---------------------------------------------------------------------------

/** "0:07", "2:15", "1:02:03" — the live clock format the generation overlay uses. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes)
  return `${hours > 0 ? `${hours}:` : ''}${mm}:${String(seconds).padStart(2, '0')}`
}

/** "45s", "2m 15s", "1h 2m" — finished spans in lists. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  if (total < 60) return `${total}s`
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`
}

/** Milliseconds between two ISO stamps; an open span runs to `now`. Null when it never started. */
export function spanMs(
  startedAt: string | null | undefined,
  completedAt: string | null | undefined,
  now: number,
): number | null {
  if (!startedAt) return null
  const start = Date.parse(startedAt)
  if (Number.isNaN(start)) return null
  const end = completedAt ? Date.parse(completedAt) : now
  return Math.max(0, (Number.isNaN(end) ? now : end) - start)
}

export function formatOliveCost(olives: number): string {
  const rounded = Math.round(olives * 10) / 10
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
}

export function formatTokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`
  if (count >= 1_000) return `${Math.round(count / 1_000)}k`
  return String(count)
}

/** The run scope; the plan UI renders its estimate separately with the currency icon. */
export function planHeadline(plan: WorkflowRunStatusPlan): string {
  if (plan.unitCount === 0) return 'nothing to run'
  const runs = plan.unitCount === 1 ? 'runs once' : `runs ${plan.unitCount}×`
  const outputs =
    plan.totalPlannedOutputs > 0
      ? ` → ${plan.totalPlannedOutputs} ${plan.totalPlannedOutputs === 1 ? 'output' : 'outputs'}`
      : ''
  return `${runs}${outputs}`
}

/** The harness's live phase line ("unit 3/7 · judging take 2"), sentence-cased. */
export function formatPhase(phase: string | null | undefined): string | null {
  if (!phase) return null
  return phase.charAt(0).toUpperCase() + phase.slice(1)
}

const PROGRESS_STAGE_LABELS: Record<WorkflowProgressStage, string> = {
  starting: 'Starting',
  planning: 'Planning',
  executing: 'Working',
  generating: 'Waiting for generations',
  reviewing: 'Reviewing',
  recovering: 'Recovering',
  finalizing: 'Finishing',
}

/**
 * The stage of a live run in two words, for the status strip. The harness's
 * operation line ("fable approved 1.41 olives for 14 takes…") belongs in
 * Activity, not under the title.
 */
export function progressStageLabel(summary: WorkflowRunStatusSummary | null | undefined): string | null {
  const stage = summary?.progress?.stage
  return stage ? PROGRESS_STAGE_LABELS[stage] : null
}

// ---------------------------------------------------------------------------
// Activity — the harness trace, humanized
// ---------------------------------------------------------------------------

export type ActivityTone = 'default' | 'muted' | 'success' | 'error'

export interface ActivityLine {
  at?: number
  stepId?: string
  technical?: boolean
  /** Step context for the whole workflow log. */
  stepLabel?: string
  key: string
  /** Milliseconds since the action began (first event of a folded group). */
  t: number
  title: string
  detail?: string
  /** Wall time of the event, or the folded group's total. */
  ms?: number
  tone: ActivityTone
  /** >1 when consecutive identical events were folded into one line. */
  count: number
  /** Outputs the event produced, for thumbnails. */
  outputAssetIds?: string[]
}

const TOOL_TITLES: Record<string, string> = {
  get_models: 'Checked available models',
  get_asset: 'Read an asset',
  view_asset: 'Looked at an asset',
  inspect_video: 'Watched a video',
  get_board_assets: 'Read the board',
  get_board_overview: 'Read the board',
  get_canvas_children: 'Read the canvas',
  get_bin_children: 'Read a bin',
  create_node: 'Created a node',
  create_nodes_and_generate: 'Started generating',
  generate_nodes: 'Started generating',
  get_jobs: 'Checked on generations',
  crop_image: 'Cropped the image',
  move_node_to_bin: 'Moved an output into its bin',
  set_asset_metadata: 'Named the outputs',
  layout_bin_children: 'Tidied the bin',
  create_text_note: 'Wrote a note',
  checkpoint_timeline: 'Saved a timeline checkpoint',
  apply_timeline_edit: 'Cut the sequence',
  create_bin: 'Created a bin',
  update_draft: 'Updated a draft',
  extract_video_frame: 'Pulled a frame from a video',
  computer_exec: 'Ran a command',
  computer_read_file: 'Read a file',
  computer_write_file: 'Wrote a file',
  check_storage_reachability: 'Checked storage',
  upload_assets_prepare: 'Prepared an upload',
  upload_assets_complete: 'Delivered a file',
  add_comment: 'Left a comment',
  flag_for_revision: 'Flagged the inputs for revision',
  run_compiled_step: 'Ran a compiled step',
  wait_for_outputs: 'Waited for generations',
  transcribe_assets: 'Transcribed the audio',
  recreate_drafts: 'Recreated the drafts',
  judge: 'Judged the takes',
  create_nodes: 'Added nodes to the board',
  create_variable: 'Created a variable',
  get_variable: 'Read a variable',
  update_variable: 'Updated a variable',
  get_timeline_context: 'Read the sequence',
  get_timeline_delivery_status: 'Checked on the render',
  render_timeline: 'Rendered the sequence',
  import_timeline_artifact: 'Brought the render onto the board',
  analyze_video_audio: 'Checked the audio against the voice lines',
  analyze_video_frames: 'Studied the frames',
  append_run_log: 'Wrote to the run log',
  report_progress: 'Reported progress',
  list_project_files: 'Listed the project files',
  read_project_file: 'Read a project file',
  write_project_file: 'Wrote a project file',
  set_file_input: 'Set a file input',
  // The sandbox agent's own tools.
  Bash: 'Ran a command',
  Read: 'Read a file',
  Write: 'Wrote a file',
  Edit: 'Edited a file',
  Grep: 'Searched the files',
  Glob: 'Listed files',
  // Workflow Beta's CLI, as the kit's scripts call it.
  martini: 'Ran a Martini command',
  'martini run': 'Ran a step script',
  'martini apply': 'Applied the step plan',
  'martini judge': 'Judged the takes',
  'martini wait': 'Waited for generations',
  'martini action': 'Reported the step status',
  'martini inputs': 'Read the inputs',
  'martini budget': 'Checked the budget',
  'martini test': 'Ran the kit tests',
  'martini effort': 'Adjusted thinking depth',
  'martini log': 'Wrote to the run log',
  'martini note': 'Left a note on the step',
  'martini comment': 'Queued a comment',
  'martini comments': 'Listed the comments',
}

/** What failed, as a noun phrase: "A step script failed", never "Martini run failed". */
const TOOL_FAILURE_SUBJECTS: Record<string, string> = {
  computer_exec: 'A command',
  Bash: 'A command',
  get_asset: 'Reading an asset',
  create_nodes: 'Adding nodes to the board',
  move_node_to_bin: 'Moving an output into its bin',
  apply_timeline_edit: 'Cutting the sequence',
  render_timeline: 'Rendering the sequence',
  add_comment: 'Leaving a comment',
  analyze_video_audio: 'The audio check',
  'martini run': 'A step script',
  'martini apply': 'Applying the step plan',
  'martini judge': 'Judging the takes',
  'martini wait': 'Waiting for generations',
  'martini effort': 'Adjusting thinking depth',
  'martini action': 'Reporting the step status',
  run_compiled_step: 'A compiled step',
  generate_nodes: 'Generation',
  create_nodes_and_generate: 'Generation',
  transcribe_assets: 'Transcription',
  wait_for_outputs: 'Waiting for generations',
}

/** Notes the harness files under a name, titled by what happened rather than by their text. */
const NOTE_TITLES: Record<string, string> = {
  repair: 'Repaired the kit',
  effort: 'Adjusted thinking depth',
  approval: 'Approved generation',
  'decision-point': 'Asked the model to judge',
  'script-tail': 'Script output',
  'agent · request': 'Model request',
  'agent · silent': 'Script still running',
  'agent · exit': 'Session closed',
  'close · memory': 'Memory use',
}

/**
 * The engine's own phases inside one CLI call (`step 02 · uploads`), named by what
 * each did for the board. They are plumbing under the visible "Applied the step
 * plan" line, so they read as technical and hide by default.
 */
const PHASE_TITLES: Record<string, string> = {
  inputs: 'Read the inputs',
  uploads: 'Uploaded the files',
  board: 'Checked the board',
  drafts: 'Prepared the drafts',
  manifests: 'Applied the plan',
  receipts: 'Recorded the results',
  resume: 'Picked up pending work',
  preload: 'Preloaded the files',
  listing: 'Listed the step files',
  environment: 'Prepared the environment',
  log: 'Wrote to the run log',
}

/** Diagnostics around each CLI call (`run · admission`); the harness's bookkeeping is `boundary · …`. */
const RUN_TITLES: Record<string, string> = {
  environment: 'Prepared the sandbox',
  admission: 'Waited for a sandbox slot',
  script: 'Ran the step script',
  integrity: 'Verified the step files',
  sandbox: 'Sandbox latency',
}
const BOUNDARY_TITLE = 'Saved the run state'

/** An engine span's title, or null for an agent tool call. */
function engineSpanTitle(name: string): string | null {
  const step = /^step \d+ · (.+)$/.exec(name)
  if (step) return PHASE_TITLES[step[1]] ?? prettifyName(step[1])
  const run = /^run · (.+)$/.exec(name)
  if (run) return RUN_TITLES[run[1]] ?? prettifyName(run[1])
  if (name.startsWith('boundary · ')) return BOUNDARY_TITLE
  return null
}

/** The MCP server records the same call the agent already reported (`mcp__martini__add_comment`). */
function toolName(name: string): string {
  return name.replace(/^mcp__martini__/, '')
}

/** Plumbing: shown on request, never by default. Every line keeps its raw name in the detail for support. */
const TECHNICAL_TOOLS = new Set([
  'Bash',
  'Read',
  'Write',
  'Edit',
  'Grep',
  'Glob',
  'computer_exec',
  'computer_read_file',
  'computer_write_file',
  'upload_assets_prepare',
  'check_storage_reachability',
  'martini effort',
  'martini inputs',
  'martini action',
])
const TECHNICAL_NOTES = new Set(['effort', 'agent · request', 'agent · silent', 'agent · exit', 'close · memory'])

function isTechnical(event: ActionTraceEvent): boolean {
  const name = event.name ?? ''
  if (event.kind === 'tool')
    return TECHNICAL_TOOLS.has(name) || name.startsWith('mcp__') || engineSpanTitle(name) !== null
  if (event.kind === 'note') return TECHNICAL_NOTES.has(name)
  return false
}

/** "01: fable approved 1.41 olives for 14 takes within the run budget: 14 Typecast voice lines · EP95cda9" → what and what it cost. */
function approvalTitle(note: string): string {
  const match = /approved ([\d.]+) olives for \d+ takes? within the run budget: (.+)$/.exec(note)
  if (!match) return 'Approved generation'
  const what = match[2]
    .split(' · ')
    .filter(part => !/^EP[0-9a-z]+$/i.test(part))
    .join(' · ')
  return `Approved ${what} · ${formatOliveCost(Number(match[1]))} olives`
}

const TOOL_TITLES_PLURAL: Record<string, (count: number) => string> = {
  get_asset: count => `Read ${count} assets`,
  view_asset: count => `Looked at ${count} assets`,
  inspect_video: count => `Watched ${count} videos`,
  create_node: count => `Created ${count} nodes`,
  move_node_to_bin: count => `Moved ${count} outputs into their bin`,
  create_text_note: count => `Wrote ${count} notes`,
  transcribe_assets: count => `Transcribed ${count} files`,
  run_compiled_step: count => `Ran ${count} compiled steps`,
  'martini run': count => `Ran ${count} step scripts`,
  'martini apply': count => `Applied ${count} step plans`,
}

function prettifyName(name: string): string {
  const words = name.replace(/[_-]+/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function unitNumber(name: string | undefined): string {
  const match = /^unit-(\d+)$/.exec(name ?? '')
  return match ? String(Number(match[1]) + 1) : ''
}

/** Consecutive tool calls of the same name, outcome, and call summary fold into one line — two commands stay two lines. */
const FOLDABLE_NOTES = new Set(['effort', 'agent · request', 'model-continuation'])

function foldKey(event: ActionTraceEvent): string {
  const name = event.name ?? ''
  return name.startsWith('boundary · ') ? 'boundary' : name
}

function foldable(a: ActionTraceEvent, b: ActionTraceEvent): boolean {
  if (a.kind === 'turn' && b.kind === 'turn') return true
  if (a.kind === 'note' && b.kind === 'note')
    return a.name === b.name && FOLDABLE_NOTES.has(a.name ?? '') && !!a.decisionPoint === !!b.decisionPoint
  return (
    a.kind === 'tool' &&
    b.kind === 'tool' &&
    foldKey(a) === foldKey(b) &&
    (a.note === 'error') === (b.note === 'error') &&
    a.detail === b.detail
  )
}

function describeEvent(
  event: ActionTraceEvent,
  count: number,
  outputCount: number,
): Pick<ActivityLine, 'title' | 'detail' | 'tone'> {
  const note = event.note ?? ''
  switch (event.kind) {
    case 'plan':
      return { title: 'Planned the run', detail: note || undefined, tone: 'default' }
    case 'tool': {
      const name = toolName(event.name ?? 'tool')
      const engine = engineSpanTitle(name)
      if (engine && note !== 'error')
        return { title: count > 1 ? `${engine} ×${count}` : engine, detail: event.detail, tone: 'muted' }
      if (note === 'error') {
        return {
          title: `${TOOL_FAILURE_SUBJECTS[name] ?? prettifyName(name)} failed${count > 1 ? ` ×${count}` : ''}`,
          detail: event.detail ?? 'The agent kept going.',
          tone: 'error',
        }
      }
      if (name === 'crop_image' && outputCount > 0) {
        return { title: `Cropped into ${outputCount} frame${outputCount === 1 ? '' : 's'}`, tone: 'default' }
      }
      const base = TOOL_TITLES[name] ?? prettifyName(name)
      const plural = TOOL_TITLES_PLURAL[name]
      const title = count > 1 ? (plural ? plural(count) : `${base} ×${count}`) : base
      return { title, detail: event.detail, tone: 'default' }
    }
    case 'turn':
      return {
        title: count > 1 ? `Thinking · ${count} turns` : 'Thinking',
        detail: `${formatTokens(event.inputTokens ?? 0)} tokens in · ${formatTokens(event.outputTokens ?? 0)} out`,
        tone: 'default',
      }
    case 'unit': {
      const unit = unitNumber(event.name)
      const subject = unit ? `Unit ${unit}` : 'Unit'
      if (note.startsWith('failed')) {
        return { title: `${subject} failed`, detail: note.replace(/^failed:\s*/, '') || undefined, tone: 'error' }
      }
      if (note.startsWith('declined')) {
        return { title: `${subject} declined`, detail: note.replace(/^declined:\s*/, '') || undefined, tone: 'muted' }
      }
      const take = /take (\d+)/.exec(note)?.[1]
      const tokens =
        event.inputTokens != null || event.outputTokens != null
          ? `${formatTokens(event.inputTokens ?? 0)} tokens in · ${formatTokens(event.outputTokens ?? 0)} out`
          : undefined
      return { title: `${subject} finished${take ? ` (take ${take})` : ''}`, detail: tokens, tone: 'success' }
    }
    case 'revision': {
      const unit = unitNumber(event.name)
      return {
        title: `${unit ? `Unit ${unit}` : 'Unit'} flagged for revision`,
        detail: note || undefined,
        tone: 'muted',
      }
    }
    case 'settle': {
      const failed = Number(/(\d+) failed/.exec(note)?.[1] ?? 0)
      return { title: 'Waited for generations', detail: note || undefined, tone: failed > 0 ? 'error' : 'default' }
    }
    case 'judge': {
      const take = /take (\d+)/.exec(note)?.[1]
      const subject = take ? `Judged take ${take}` : 'Judge'
      if (/:\s*pass\b/.test(note)) return { title: `${subject} · pass`, tone: 'success' }
      if (/reject/.test(note)) {
        return {
          title: `${subject} · rejected`,
          detail: note.replace(/^take \d+:\s*reject\s*—?\s*/, '') || undefined,
          tone: 'error',
        }
      }
      return { title: subject, detail: note || undefined, tone: 'muted' }
    }
    case 'sweep':
      return { title: 'Routed outputs into the bin', detail: note || undefined, tone: 'muted' }
    case 'note':
      if (note.startsWith('error:')) return { title: 'Error', detail: note.slice(6).trim(), tone: 'error' }
      if (/^materialized tools\/?:/.test(note))
        return { title: 'Tools ready', detail: note.replace(/^materialized tools\/?:\s*/, ''), tone: 'muted' }
      if (note === 'resumed') return { title: 'Resumed', tone: 'muted' }
      if (event.name === 'model-continuation') {
        // A model step between tool calls: the harness names the tool it reached for next.
        const next = event.firstTool
          ? (TOOL_TITLES[toolName(event.firstTool)] ?? prettifyName(toolName(event.firstTool)))
          : null
        const title = event.decisionPoint ? 'Judged the results' : 'Thinking'
        return {
          title: count > 1 ? `${title} · ${count} turns` : title,
          detail: next ? `Then: ${next.charAt(0).toLowerCase()}${next.slice(1)}` : undefined,
          tone: 'default',
        }
      }
      if (event.name === 'approval') return { title: approvalTitle(note), detail: note, tone: 'default' }
      if (event.name && NOTE_TITLES[event.name]) {
        // The harness's own line ("effort requested: low…") is the detail, one click away.
        const title = NOTE_TITLES[event.name]
        return { title: count > 1 ? `${title} ×${count}` : title, detail: note || event.detail, tone: 'muted' }
      }
      // The truncation marker carries its dropped count; every other note carries a detail worth showing too.
      return {
        title: note || 'Note',
        detail: event.dropped ? `${event.dropped.toLocaleString('en-US')} earlier events not stored` : event.detail,
        tone: 'muted',
      }
    default:
      return { title: prettifyName(event.kind), detail: note || undefined, tone: 'muted' }
  }
}

function isTraceEvent(value: unknown): value is ActionTraceEvent {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ActionTraceEvent).t === 'number' &&
    typeof (value as ActionTraceEvent).kind === 'string'
  )
}

/** Humanizes a run action's trace into lines the inspector can render — folding repeats, naming tools by what they did. */
export function buildActivity(trace: unknown[] | null | undefined): ActivityLine[] {
  const events = (Array.isArray(trace) ? trace : []).filter(isTraceEvent)
  const lines: ActivityLine[] = []
  let index = 0
  while (index < events.length) {
    const first = events[index]
    let end = index + 1
    while (end < events.length && foldable(first, events[end])) end += 1
    const group = events.slice(index, end)
    const ms = group.some(event => event.ms != null)
      ? group.reduce((sum, event) => sum + (event.ms ?? 0), 0)
      : undefined
    const outputAssetIds = group.flatMap(event => event.outputAssetIds ?? [])
    lines.push({
      key: `${index}-${first.kind}-${first.name ?? ''}`,
      t: first.t,
      at: first.at,
      technical: isTechnical(first),
      ms,
      count: group.length,
      outputAssetIds: outputAssetIds.length > 0 ? outputAssetIds : undefined,
      ...withRawName(
        first,
        isTechnical(first),
        describeEvent(
          first.kind === 'turn'
            ? {
                ...first,
                inputTokens: group.reduce((sum, event) => sum + (event.inputTokens ?? 0), 0),
                outputTokens: group.reduce((sum, event) => sum + (event.outputTokens ?? 0), 0),
              }
            : first,
          group.length,
          outputAssetIds.length,
        ),
      ),
    })
    index = end
  }
  return lines
}

/** A technical line without its own detail keeps the raw event name, so support can still read the span. */
function withRawName(
  event: ActionTraceEvent,
  technical: boolean,
  described: Pick<ActivityLine, 'title' | 'detail' | 'tone'>,
): Pick<ActivityLine, 'title' | 'detail' | 'tone'> {
  if (described.detail || !technical || !event.name || event.name === described.title) return described
  return { ...described, detail: event.name }
}

// ---------------------------------------------------------------------------
// Revision requests — units the agent ended on purpose
// ---------------------------------------------------------------------------

export interface ItemRevisionRequest {
  item: WorkflowRunItemRecord
  request: WorkflowRunRevisionRequest
}

/** The items of an action the agent flagged with flag_for_revision, with their parsed requests. */
export function revisionRequests(items: readonly WorkflowRunItemRecord[]): ItemRevisionRequest[] {
  return items.flatMap(item => {
    const request = workflowRunRevisionRequestOf(item)
    return request ? [{ item, request }] : []
  })
}

// ---------------------------------------------------------------------------
// Where an action stands inside a mirrored run
// ---------------------------------------------------------------------------

export interface ActionStanding {
  runId: string
  updatedAt: string
  runStatus: WorkflowRunStatus['status']
  /** This action's own status inside the run; the run's status while it is the current action. */
  actionStatus: string
  isCurrent: boolean
  /** The action that has to finish first, while this one is waiting. */
  blockedBy: string | null
  slot: WorkflowRunStatusAction | null
  status: WorkflowRunStatus
}

/**
 * Which of two mirrored runs a panel should show: a live run before a finished
 * one, then the newer run. "Newer" is run creation, not the mirror's
 * `updatedAt` — two live runs on one canvas (an API run beside a canvas run)
 * take turns syncing, and ordering by `updatedAt` made the panels oscillate
 * between them every second. Mirrors written before `createdAt` existed fall
 * back to their start, then their last sync.
 */
export function compareRunStanding(a: WorkflowRunStatus, b: WorkflowRunStatus): number {
  const aLive = runIsActive(a.status) ? 1 : 0
  const bLive = runIsActive(b.status) ? 1 : 0
  if (aLive !== bLive) return bLive - aLive
  const key = (status: WorkflowRunStatus) => status.createdAt ?? status.startedAt ?? status.updatedAt
  return key(b).localeCompare(key(a)) || b.updatedAt.localeCompare(a.updatedAt)
}

/**
 * The run this action stands in: the live one if any, else the newest
 * finished one (see compareRunStanding). Statuses mirrored before `actions`
 * existed only know the current action, so they match while current.
 */
export function findActionStanding(statuses: Iterable<WorkflowRunStatus>, actionId: string): ActionStanding | null {
  let best: ActionStanding | null = null
  for (const status of statuses) {
    if (!mirrorStands(status)) continue
    const slots = status.actions ?? []
    const slotIndex = slots.findIndex(slot => slot.actionId === actionId)
    const isCurrent = status.currentActionId === actionId
    if (slotIndex === -1 && !isCurrent) continue
    if (best && compareRunStanding(best.status, status) <= 0) continue
    const slot = slotIndex >= 0 ? slots[slotIndex] : null
    const actionStatus = status.build
      ? buildActionStatus(status)
      : actionStatusInRun(slot?.status ?? 'pending', status.status, isCurrent)
    const waiting = !isCurrent && actionStatus === 'pending'
    best = {
      runId: status.runId,
      updatedAt: status.updatedAt,
      runStatus: status.status,
      actionStatus,
      isCurrent,
      blockedBy: waiting && slotIndex > 0 ? slots[slotIndex - 1].actionId : null,
      slot,
      status,
    }
  }
  return best
}

/**
 * Whose run it is, in one line for the panel: who started it and whose olives
 * it spends, then who approved the current plan or who cancelled it. Names
 * come from the mirror; a viewer's own name reads "you". Null when the mirror
 * predates identity.
 */
export function runPeopleLine(
  status: Pick<WorkflowRunStatus, 'status' | 'startedBy' | 'payer' | 'approvedBy' | 'cancelledBy'> | null | undefined,
  viewerUserId: string | null | undefined,
): string | null {
  if (!status?.startedBy) return null
  const name = (person: { id: string; name: string }) => (person.id === viewerUserId ? 'you' : person.name)
  const parts = [`Started by ${name(status.startedBy)}`]
  if (status.payer && status.status !== 'cancelled') parts.push(`billed to ${status.payer.name}`)
  if (status.status === 'cancelled' && status.cancelledBy) parts.push(`cancelled by ${name(status.cancelledBy)}`)
  else if (status.approvedBy && status.approvedBy.id !== status.startedBy.id) {
    parts.push(`approved by ${name(status.approvedBy)}`)
  }
  return parts.join(' · ')
}

// ---------------------------------------------------------------------------
// A run as a chat card — from whichever source knows it best
// ---------------------------------------------------------------------------

/** What `run_workflow` handed back before any progress existed. */
export interface RunCardFallback {
  runId: string
  status: string
  canvasId: string | null
  actionCount: number | null
  oliveBudget: number | null
}

export interface RunCardModel {
  runId: string
  status: string
  /** Live while the harness is working on it (spinner). */
  live: boolean
  /** True while the run can still change — polling and the mirror matter. */
  active: boolean
  /** 1-based position of the current action; null before the run claims one or once it has ended. */
  actionNumber: number | null
  actionCount: number | null
  currentActionName: string | null
  phase: string | null
  itemsDone: number
  itemsFailed: number
  itemsTotal: number
  /** Generation + agent olives. */
  spentOlives: number
  budgetOlives: number | null
  errorMessage: string | null
  sceneId: string | null
  /** Every action in the run, for "Open on canvas"; empty when only the fallback is known. */
  actionIds: string[]
  /** The parked plan while the run awaits approval: how many units, and what the reading was unsure about. */
  gate: { unitCount: number; ambiguity: string | null; overBudget?: ActionPlanOverBudget | null } | null
}

/**
 * The run card reads the backend-owned mirror first (live, no HTTP), the
 * polled details next (a run the mirror has not written yet), and the
 * `run_workflow` result last (the moment right after the call). Action names
 * come from the canvas when it has them, else from the run's own snapshot.
 */
export function runCardModel({
  mirror,
  details,
  actions,
  fallback,
}: {
  mirror: WorkflowRunStatus | null | undefined
  details: WorkflowRunDetails | null | undefined
  actions: ReadonlyMap<string, Pick<Action, 'name'>>
  fallback: RunCardFallback
}): RunCardModel {
  const status = mirror?.status ?? details?.run.status ?? fallback.status
  const active = runIsActive(status)
  const live = statusMeta(status).live
  const snapshotActions = details?.run.definitionSnapshot.actions ?? []
  const actionIds = mirror?.actions
    ? mirror.actions.map(slot => slot.actionId)
    : details
      ? details.actions.map(entry => entry.actionId)
      : []
  const actionCount = mirror?.actionCount ?? (details ? details.actions.length : fallback.actionCount)
  const currentActionId = mirror
    ? mirror.currentActionId
    : (details?.actions[details.run.currentActionIndex]?.actionId ?? null)
  const currentIndex = mirror ? mirror.currentActionIndex : (details?.run.currentActionIndex ?? null)
  const actionNumber =
    active && currentActionId && currentIndex != null && actionCount != null && currentIndex < actionCount
      ? currentIndex + 1
      : null
  const currentActionName = currentActionId
    ? (actions.get(currentActionId)?.name ??
      details?.actions.find(entry => entry.actionId === currentActionId)?.definition.name ??
      snapshotActions.find(entry => entry.id === currentActionId)?.name ??
      null)
    : null
  const items = details?.items ?? []
  const itemsDone = mirror ? mirror.completedItemCount : items.filter(item => item.status === 'completed').length
  const itemsFailed = mirror ? mirror.failedItemCount : items.filter(item => item.status === 'failed').length
  const itemsTotal = mirror ? mirror.totalItemCount : items.length
  const currentPlan = details?.actions[details.run.currentActionIndex]?.plan ?? null
  const gate =
    status === 'awaiting_approval'
      ? mirror?.plan
        ? { unitCount: mirror.plan.unitCount, ambiguity: mirror.plan.ambiguity, overBudget: mirror.plan.overBudget }
        : currentPlan
          ? {
              unitCount: currentPlan.units.length,
              ambiguity: currentPlan.ambiguity,
              overBudget: currentPlan.overBudget,
            }
          : null
      : null
  const generation =
    details?.generationBilling?.chargedOlives ?? mirror?.generationCostOlives ?? details?.run.generationCostOlives ?? 0
  // A run with a receipt shows the receipt's figure everywhere; the mirror's measurement is the fallback.
  const agent = isUnifiedAgentBilling(details?.billing)
    ? agentSpendOlives(details.billing)
    : (mirror?.agentCostOlives ?? details?.run.agentCostOlives ?? 0)
  return {
    runId: fallback.runId,
    status,
    live,
    active,
    actionNumber,
    actionCount,
    currentActionName,
    phase: live ? formatPhase(mirror?.phase) : null,
    itemsDone,
    itemsFailed,
    itemsTotal,
    spentOlives: generation + agent,
    budgetOlives: mirror ? mirror.oliveBudget : (details?.run.oliveBudget ?? fallback.oliveBudget),
    errorMessage: mirror?.errorMessage ?? details?.run.errorMessage ?? null,
    sceneId: mirror?.sceneId ?? details?.run.sceneId ?? fallback.canvasId,
    actionIds,
    gate,
  }
}

/**
 * What a parked run needs from the user. A zero-unit plan cannot be approved
 * (the harness refuses Go), so the line names the fix the plan already
 * spelled out — usually an empty input bin — instead of a mute disabled
 * button.
 */
export function gateLine(
  gate: { unitCount: number; ambiguity: string | null; overBudget?: ActionPlanOverBudget | null } | null,
): string | null {
  if (!gate) return null
  if (gate.unitCount === 0) return `Go is unavailable: ${gate.ambiguity ?? 'the plan found nothing to run on.'}`
  if (gate.overBudget) return overBudgetLine(gate.overBudget)
  return gate.ambiguity
}

/**
 * The park that is about price, not reading: the agent built the batch, the
 * server priced it over what was left, nothing was generated. Go re-arms the
 * rail over the exact cost — the same button as every other park.
 */
export function overBudgetLine(overBudget: ActionPlanOverBudget): string {
  return `Generating costs ${overBudget.costOlives} olives, ${overBudget.remainingOlives} left on this run's budget. Go raises the budget to cover it.`
}

/** One chronological log with timestamps relative to the workflow start. */
export function buildWorkflowActivity(details: WorkflowRunDetails | null): ActivityLine[] {
  if (!details) return []
  const origin = Date.parse(details.run.startedAt ?? details.run.createdAt)
  return details.actions
    .flatMap(action => {
      const start = Date.parse(action.startedAt ?? details.run.startedAt ?? details.run.createdAt)
      const offset = Number.isFinite(start - origin) ? Math.max(0, start - origin) : 0
      return buildActivity(action.trace).map(line => ({
        ...line,
        key: `${action.actionId}:${line.key}`,
        t: line.at != null && Number.isFinite(line.at - origin) ? Math.max(0, line.at - origin) : offset + line.t,
        stepId: action.actionId,
        // The run's own index: `definition.order` counts across the canvas.
        stepLabel: `Step ${action.actionIndex + 1} · ${actionDisplayName(action.definition.name)}`,
      }))
    })
    .sort((a, b) => a.t - b.t)
}
